import { Suspense } from 'react';
import { jsonToHtml } from '@contentstack/json-rte-serializer';
import {
  useProduct,
  useSelectedVariant
} from '@/components/product/product-context';
import { useModal, MODAL_TYPES } from '@/components/modal/ModalProvider';
import offsetBlockClass from '@/utils/offsetBlockClass';
import { findSelectorOption, readSelectorSettings } from '../selector-settings';
import HexSwatches from './HexSwatches';
import ImageSwatches from './ImageSwatches';

const SWATCH_GRIDS = {
  hex: HexSwatches,
  image: ImageSwatches
};

/**
 * A color selector rendered as a row of swatches, either flat hex circles or
 * Contentstack images.
 *
 * Fully static: the section, the label and the help modal link all come from
 * our CMS, so the only things behind a boundary are the selected value in the
 * heading and what's selected.
 */
export default function Swatches({ block, position }) {
  const { productData } = useProduct();
  const { showModal } = useModal();

  const { settings, selectorSettings } = readSelectorSettings(block);
  const {
    label,
    modal,
    option: optionName,
    show_offset_block_selector
  } = settings;

  if (!findSelectorOption(productData, optionName, 'swatches')) {
    return null;
  }

  const handleModalClick = () => {
    const htmlContent =
      typeof modal.modal_content === 'object'
        ? jsonToHtml(modal.modal_content)
        : modal.modal_content;

    showModal(MODAL_TYPES.secondarySelector, {
      modalContent: htmlContent,
      modalHeading: modal.modal_heading
    });
  };

  const SwatchGrid = SWATCH_GRIDS[selectorSettings.swatch_type];

  return (
    <section
      id={`${selectorSettings._metadata?.uid}_${position}`}
      data-name="secondary-selector"
      data-cy="color-swatches-selector"
      className={`secondary-selector colors ${offsetBlockClass(show_offset_block_selector, 'not-last:mb-8')}`}
    >
      <div className="relative text-left">
        <span className="sh12 text-crisp-white inline-block">
          {label}&nbsp;
        </span>
        {/* The label sibling holds the line height, so this can resolve to
            nothing without shifting the row. */}
        <Suspense fallback={null}>
          <SelectedValue optionName={optionName} />
        </Suspense>
        {modal?.link_copy && (
          <button
            onClick={handleModalClick}
            className="body-s text-dark-grey absolute top-0 right-0 cursor-pointer underline"
          >
            {modal.link_copy}
          </button>
        )}
      </div>
      <div className="mt-2.5 flex w-full min-w-0 flex-1 flex-col">
        {SwatchGrid ? (
          <SwatchGrid
            selectorSettings={selectorSettings}
            optionName={optionName}
          />
        ) : null}
      </div>
    </section>
  );
}

/**
 * The value currently picked, shown next to the label. Reads the selected
 * variant, so it needs to stay inside a Suspense boundary.
 */
const SelectedValue = ({ optionName }) => {
  const { variant } = useSelectedVariant();
  const currentValue = variant.selectedOptions.find(
    (o) => o.name === optionName
  )?.value;

  return (
    <span
      className="body-s font-weight-400 text-crisp-white inline-block"
      data-cy="color-swatches-value"
    >
      {currentValue}
    </span>
  );
};
